import PropTypes from 'prop-types';
import React, { createContext, useEffect, useReducer } from 'react';
import { jwtDecode } from 'jwt-decode';
import { LOGIN, LOGOUT } from 'store/actions';
import accountReducer from 'store/accountReducer';
import Loader from 'ui-component/Loader';
import axiosServices from 'utils/axios';
import { getSubdomainFromHost } from 'utils/org';

const initialState = {
  isLoggedIn: false,
  isInitialized: false,
  user: null,
  access_token: '',
  refresh_token: ''
};

function verifyToken(serviceToken) {
  if (!serviceToken) return false;
  try {
    const decoded = jwtDecode(serviceToken);
    if (!decoded.exp) return false;
    return decoded.exp > Date.now() / 1000;
  } catch (err) {
    return false;
  }
}

function setSession(serviceToken) {
  if (serviceToken) {
    localStorage.setItem('serviceToken', serviceToken);
    axiosServices.defaults.headers.common.Authorization = `Bearer ${serviceToken}`;
  } else {
    localStorage.removeItem('serviceToken');
    delete axiosServices.defaults.headers.common.Authorization;
  }
}

function getCurrentSubdomain() {
  return getSubdomainFromHost(window.location.hostname);
}

function mapSessionUser(responseData, fallbackToken = '') {
  let decoded = {};
  if (fallbackToken) {
    try {
      decoded = jwtDecode(fallbackToken);
    } catch (err) {
      decoded = {};
    }
  }

  const currentMembership = responseData.current_membership ? {
    id: responseData.current_membership.id,
    user_id: responseData.current_membership.user_id,
    org_id: responseData.current_membership.org_id,
    role: responseData.current_membership.role,
    role_pt: responseData.current_membership.role_pt,
    org_name: responseData.current_membership.org_name,
    status_ids: responseData.current_membership.status_ids
  } : null;

  const memberships = Array.isArray(responseData.memberships) 
    ? responseData.memberships.map(m => ({
        id: m.id,
        user_id: m.user_id,
        org_id: m.org_id,
        role: m.role,
        role_pt: m.role_pt,
        org_name: m.org_name
      }))
    : [];

  return {
    ...decoded,
    ...(responseData.user || {}),
    id: responseData.user?.id ?? (decoded?.sub ? Number(decoded.sub) : undefined),
    email: responseData.user?.email || decoded?.email || '',
    first_name: responseData.user?.first_name || decoded?.first_name || '',
    last_name: responseData.user?.last_name || decoded?.last_name || '',
    full_name:
      responseData.user?.full_name ||
      [responseData.user?.first_name, responseData.user?.last_name].filter(Boolean).join(' ').trim() ||
      decoded?.full_name ||
      '',
    current_org: responseData.current_org || null,
    current_membership: currentMembership,
    orgs: responseData.orgs || [],
    memberships: memberships
  };
}

async function enrichStoredUserFromCurrentEndpoint(serviceToken, baseUser) {
  setSession(serviceToken);

  try {
    const { data } = await axiosServices.get('/api/v1/users/current');

    const currentMembership = data.current_membership ? {
      id: data.current_membership.id,
      user_id: data.current_membership.user_id,
      org_id: data.current_membership.org_id,
      role: data.current_membership.role,
      role_pt: data.current_membership.role_pt,
      org_name: data.current_membership.org_name,
      status_ids: data.current_membership.status_ids
    } : (baseUser?.current_membership || null);

    const memberships = Array.isArray(data.memberships)
      ? data.memberships.map(m => ({
          id: m.id,
          user_id: m.user_id,
          org_id: m.org_id,
          role: m.role,
          role_pt: m.role_pt,
          org_name: m.org_name
        }))
      : (baseUser?.memberships || []);

    return {
      ...baseUser,
      ...data,
      full_name:
        baseUser?.full_name ||
        data?.full_name ||
        [data?.first_name, data?.last_name].filter(Boolean).join(' ').trim() ||
        '',
      current_org: baseUser?.current_org || data?.current_org || null,
      current_membership: currentMembership,
      // Confirmado: /users/sign_in já retorna orgs completo (com env).
      // /api/v1/users/current aparentemente NÃO inclui env — por isso
      // preferimos o orgs que já veio do baseUser quando não estiver
      // vazio, e só usamos o de /users/current como fallback.
      orgs: baseUser?.orgs?.length ? baseUser.orgs : (data?.orgs || []),
      memberships: memberships
    };
  } catch (error) {
    return baseUser;
  }
}

const JWTContext = createContext(null);

export function JWTProvider({ children }) {
  const [state, dispatch] = useReducer(accountReducer, initialState);

  useEffect(() => {
    const init = async () => {
      try {
        const serviceToken = localStorage.getItem('serviceToken');

        if (serviceToken && verifyToken(serviceToken)) {
          setSession(serviceToken);

          const storedUser = localStorage.getItem('user');
          let user = storedUser ? JSON.parse(storedUser) : null;

          if (user) {
            user = await enrichStoredUserFromCurrentEndpoint(serviceToken, user);
            localStorage.setItem('user', JSON.stringify(user));
          } else {
            const decoded = jwtDecode(serviceToken);
            user = {
              ...decoded,
              id: decoded?.sub ? Number(decoded.sub) : undefined,
              email: decoded?.email || '',
              first_name: decoded?.first_name || '',
              last_name: decoded?.last_name || '',
              full_name: decoded?.full_name || '',
              current_org: null,
              current_membership: null,
              orgs: [],
              memberships: []
            };
            localStorage.setItem('user', JSON.stringify(user));
          }

          dispatch({
            type: LOGIN,
            payload: {
              isLoggedIn: true,
              isInitialized: true,
              user,
              access_token: serviceToken,
              refresh_token: ''
            }
          });
        } else {
          setSession(null);
          localStorage.removeItem('user');
          dispatch({
            type: LOGOUT,
            payload: {
              isInitialized: true,
              access_token: '',
              refresh_token: '',
              user: null
            }
          });
        }
      } catch (err) {
        setSession(null);
        localStorage.removeItem('user');
        dispatch({
          type: LOGOUT,
          payload: {
            isInitialized: true,
            access_token: '',
            refresh_token: '',
            user: null
          }
        });
      }
    };

    init();
  }, []);

  useEffect(() => {
    const syncAuth = async () => {
      const serviceToken = localStorage.getItem('serviceToken');

      if (serviceToken && verifyToken(serviceToken)) {
        setSession(serviceToken);

        const storedUser = localStorage.getItem('user');
        let user = storedUser ? JSON.parse(storedUser) : null;

        if (user) {
          user = await enrichStoredUserFromCurrentEndpoint(serviceToken, user);
          localStorage.setItem('user', JSON.stringify(user));
        } else {
          const decoded = jwtDecode(serviceToken);
          user = {
            ...decoded,
            id: decoded?.sub ? Number(decoded.sub) : undefined,
            email: decoded?.email || '',
            first_name: decoded?.first_name || '',
            last_name: decoded?.last_name || '',
            full_name: decoded?.full_name || '',
            current_org: null,
            current_membership: null,
            orgs: [],
            memberships: []
          };
          localStorage.setItem('user', JSON.stringify(user));
        }

        dispatch({
          type: LOGIN,
          payload: {
            isLoggedIn: true,
            isInitialized: true,
            user,
            access_token: serviceToken,
            refresh_token: ''
          }
        });
      } else {
        setSession(null);
        localStorage.removeItem('user');
        dispatch({
          type: LOGOUT,
          payload: {
            isInitialized: true,
            access_token: '',
            refresh_token: '',
            user: null
          }
        });

        if (window.location.pathname !== '/login') {
          window.location.replace('/login');
        }
      }
    };

    const onStorage = (event) => {
      if (event.key === 'serviceToken' || event.key === null) {
        syncAuth();
      }
    };

    const onAuthLogout = () => syncAuth();
    const onAuthLogin = () => syncAuth();

    window.addEventListener('storage', onStorage);
    window.addEventListener('auth:logout', onAuthLogout);
    window.addEventListener('auth:login', onAuthLogin);

    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('auth:logout', onAuthLogout);
      window.removeEventListener('auth:login', onAuthLogin);
    };
  }, []);

  const login = async (username, password, subdomain = getCurrentSubdomain()) => {
    if (!subdomain) {
      throw new Error('Não foi possível identificar a organização a partir do endereço acessado.');
    }

    try {
      const response = await axiosServices.post('/users/sign_in', {
        user: {
          email: username,
          password,
          subdomain
        }
      });

      const responseData = response.data;
      const serviceToken =
        responseData.token ||
        response.headers?.authorization?.replace(/^Bearer\s+/i, '') ||
        response.headers?.Authorization?.replace(/^Bearer\s+/i, '');

      if (!serviceToken) {
        throw new Error('Token JWT não retornado no login');
      }

      const rawUser = mapSessionUser(responseData, serviceToken);

      setSession(serviceToken);

      // O /users/sign_in traz um `orgs` reduzido (provavelmente só
      // subdomínios, sem env). Enriquece com /api/v1/users/current logo
      // após logar, pro user.orgs já nascer completo — senão qualquer
      // tela que dependa de campos como env (ex: resolução de domínio)
      // só funciona depois de um reload da página.
      const user = await enrichStoredUserFromCurrentEndpoint(serviceToken, rawUser);

      localStorage.setItem('user', JSON.stringify(user));

      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          isInitialized: true,
          user,
          access_token: serviceToken,
          refresh_token: ''
        }
      });

      window.dispatchEvent(new Event('auth:login'));

      return user;
    } catch (err) {
      setSession(null);
      localStorage.removeItem('user');
      dispatch({
        type: LOGOUT,
        payload: {
          isInitialized: true,
          access_token: '',
          refresh_token: '',
          user: null
        }
      });
      throw err;
    }
  };

  const requestLoginCode = async (email, subdomain = getCurrentSubdomain()) => {
    if (!subdomain) {
      throw new Error('Não foi possível identificar a organização a partir do endereço acessado.');
    }

    const response = await axiosServices.post('/auth/login-code', {
      user: { email, subdomain }
    });

    return response.data;
  };

  const verifyLoginCode = async (email, code, subdomain = getCurrentSubdomain()) => {
    if (!subdomain) {
      throw new Error('Não foi possível identificar a organização a partir do endereço acessado.');
    }

    try {
      const response = await axiosServices.post('/auth/verify-code', {
        user: { email, code, subdomain }
      });

      if (!response.data.token) throw new Error('Token JWT não retornado');

      const serviceToken = response.data.token;
      const rawUser = mapSessionUser(response.data, serviceToken);

      setSession(serviceToken);

      const user = await enrichStoredUserFromCurrentEndpoint(serviceToken, rawUser);

      localStorage.setItem('user', JSON.stringify(user));

      dispatch({
        type: LOGIN,
        payload: {
          isLoggedIn: true,
          isInitialized: true,
          user,
          access_token: serviceToken,
          refresh_token: ''
        }
      });

      window.dispatchEvent(new Event('auth:login'));

      return user;
    } catch (err) {
      setSession(null);
      localStorage.removeItem('user');
      dispatch({
        type: LOGOUT,
        payload: {
          isInitialized: true,
          access_token: '',
          refresh_token: '',
          user: null
        }
      });
      throw err;
    }
  };

  // src/contexts/JWTContext.js
  const logout = async () => {
    try {
      // Call backend logout API - using DELETE method
      await axiosServices.delete('/users/sign_out');
    } catch (err) {
      console.error('Logout error:', err);
    }

    // Client-side cleanup
    setSession(null);
    localStorage.removeItem('serviceToken');
    dispatch({
      type: LOGOUT,
      payload: {
        isInitialized: true,
        access_token: '',
        refresh_token: '',
        user: null
      }
    });

    window.dispatchEvent(new Event('auth:logout'));
    // Redirect to login page, not /users/sign_out
    window.location.replace('/login');
  };

  const updateUser = (userData) => {
    const currentMembership = userData.current_membership ? {
      id: userData.current_membership.id,
      user_id: userData.current_membership.user_id,
      org_id: userData.current_membership.org_id,
      role: userData.current_membership.role,
      role_pt: userData.current_membership.role_pt,
      org_name: userData.current_membership.org_name,
      status_ids: userData.current_membership.status_ids
    } : (state.user?.current_membership || null);

    const memberships = Array.isArray(userData.memberships)
      ? userData.memberships.map(m => ({
          id: m.id,
          user_id: m.user_id,
          org_id: m.org_id,
          role: m.role,
          role_pt: m.role_pt,
          org_name: m.org_name
        }))
      : (state.user?.memberships || []);

    const updatedUser = {
      ...state.user,
      ...userData,
      full_name: userData?.full_name || state.user?.full_name || '',
      current_org: userData?.current_org || state.user?.current_org || null,
      current_membership: currentMembership,
      orgs: userData?.orgs?.length ? userData.orgs : state.user?.orgs || [],
      memberships: memberships
    };

    localStorage.setItem('user', JSON.stringify(updatedUser));

    dispatch({
      type: LOGIN,
      payload: {
        isLoggedIn: true,
        isInitialized: true,
        user: updatedUser,
        access_token: state.access_token,
        refresh_token: state.refresh_token
      }
    });
  };

  if (!state.isInitialized) {
    return <Loader />;
  }

  return (
    <JWTContext.Provider
      value={{
        ...state,
        login,
        logout,
        requestLoginCode,
        verifyLoginCode,
        updateUser
      }}
    >
      {children}
    </JWTContext.Provider>
  );
}

export default JWTContext;

JWTProvider.propTypes = { children: PropTypes.node };